/**
 * Geocoding - provider-agnostic address suggest/resolve for the map address lookup.
 *
 * v1 ships with a Nominatim (OpenStreetMap) provider that needs no API key.
 * When VITE_ESRI_API_KEY is set, the Esri provider is used instead — it offers
 * much better typeahead. Swapping providers requires no component changes; the
 * selector below picks Esri automatically once the key is present.
 */

export interface AddressSuggestion {
  id: string;
  /** Human-readable single-line address for display. */
  label: string;
  street?: string;
  city?: string;
  /** PA township/borough/city (county subdivision) — best-effort from the geocoder. */
  municipality?: string;
  county?: string;
  state?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  /** Opaque provider data (e.g. Esri magicKey) carried from suggest() into resolve(). */
  providerData?: unknown;
}

export interface GeocodingProvider {
  name: string;
  /** Up to ~5 address suggestions for a partial query. */
  suggest(query: string, signal?: AbortSignal): Promise<AddressSuggestion[]>;
  /** Ensures the suggestion has latitude/longitude (and address parts). */
  resolve(suggestion: AddressSuggestion, signal?: AbortSignal): Promise<AddressSuggestion>;
}

// --- Nominatim (OpenStreetMap) -----------------------------------------------

interface NominatimAddress {
  house_number?: string;
  road?: string;
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  municipality?: string;
  borough?: string;
  county?: string;
  state?: string;
  postcode?: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address: NominatimAddress;
}

// Bias results toward southwestern PA (AIU service area) without hard-limiting.
const PA_VIEWBOX = '-80.9,40.0,-79.0,41.0';

function nominatimMunicipality(a: NominatimAddress): string | undefined {
  return a.municipality || a.borough || a.town || a.village || a.hamlet || a.city;
}

const nominatimProvider: GeocodingProvider = {
  name: 'nominatim',
  async suggest(query, signal) {
    if (query.trim().length < 4) return [];
    const params = new URLSearchParams({
      q: query,
      format: 'json',
      addressdetails: '1',
      countrycodes: 'us',
      limit: '5',
      viewbox: PA_VIEWBOX,
    });
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      { headers: { Accept: 'application/json' }, signal }
    );
    if (!res.ok) throw new Error(`Address lookup failed (${res.status})`);
    const data: NominatimResult[] = await res.json();
    return data.map((r) => {
      const a = r.address || {};
      const street = [a.house_number, a.road].filter(Boolean).join(' ');
      return {
        id: String(r.place_id),
        label: r.display_name,
        street: street || undefined,
        city: a.city || a.town || a.village || undefined,
        municipality: nominatimMunicipality(a),
        county: a.county || undefined,
        state: a.state || undefined,
        postalCode: a.postcode || undefined,
        latitude: parseFloat(r.lat),
        longitude: parseFloat(r.lon),
      } satisfies AddressSuggestion;
    });
  },
  async resolve(suggestion) {
    // Nominatim search already returns coordinates, so there's nothing to do.
    return suggestion;
  },
};

// --- Esri (ArcGIS World Geocoder) --------------------------------------------

const ESRI_BASE =
  'https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer';

interface EsriSuggestItem {
  text: string;
  magicKey: string;
}

interface EsriCandidate {
  location?: { x: number; y: number };
  attributes?: Record<string, string>;
}

function createEsriProvider(apiKey: string): GeocodingProvider {
  return {
    name: 'esri',
    async suggest(query, signal) {
      if (query.trim().length < 3) return [];
      const params = new URLSearchParams({
        text: query,
        countryCode: 'USA',
        category: 'Address',
        maxSuggestions: '5',
        f: 'json',
        token: apiKey,
      });
      const res = await fetch(`${ESRI_BASE}/suggest?${params.toString()}`, { signal });
      if (!res.ok) throw new Error(`Address lookup failed (${res.status})`);
      const data = await res.json();
      const items: EsriSuggestItem[] = data.suggestions || [];
      return items.map((s, i) => ({
        id: s.magicKey || String(i),
        label: s.text,
        providerData: { magicKey: s.magicKey },
      }));
    },
    async resolve(suggestion, signal) {
      const magicKey = (suggestion.providerData as { magicKey?: string })?.magicKey;
      const params = new URLSearchParams({
        f: 'json',
        token: apiKey,
        outFields: 'StAddr,City,Subregion,Region,Postal',
        maxLocations: '1',
        singleLine: suggestion.label,
      });
      if (magicKey) params.set('magicKey', magicKey);
      const res = await fetch(
        `${ESRI_BASE}/findAddressCandidates?${params.toString()}`,
        { signal }
      );
      if (!res.ok) throw new Error(`Address lookup failed (${res.status})`);
      const data = await res.json();
      const c: EsriCandidate | undefined = (data.candidates || [])[0];
      if (!c || !c.location) throw new Error('Address could not be located.');
      const attr = c.attributes || {};
      return {
        ...suggestion,
        street: attr.StAddr || undefined,
        city: attr.City || undefined,
        municipality: attr.City || undefined,
        county: attr.Subregion || undefined,
        state: attr.Region || undefined,
        postalCode: attr.Postal || undefined,
        latitude: c.location.y,
        longitude: c.location.x,
      };
    },
  };
}

// --- Selector ----------------------------------------------------------------

let cachedProvider: GeocodingProvider | null = null;

/**
 * Returns the active geocoding provider. Prefers Esri when VITE_ESRI_API_KEY is
 * configured (referrer-restricted key, safe for client use), otherwise Nominatim.
 */
export function getGeocodingProvider(): GeocodingProvider {
  if (cachedProvider) return cachedProvider;
  const esriKey = import.meta.env.VITE_ESRI_API_KEY as string | undefined;
  cachedProvider = esriKey ? createEsriProvider(esriKey) : nominatimProvider;
  return cachedProvider;
}
